import { FC, useEffect, useMemo, useState } from "react";
import AppModal from "../../../apps/components/AppModal";
import { IProjectEvaluationListData, IProjectEvaluator } from "../../../project";
import EvalStatus from "../../../apps/components/EvalStatus";
import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import { sendEvaluationReminderEmailsToAllEvaluators } from "../../../project/core/_project_evaluation";
import { useAuth } from "../../../auth";
import { resolveNumericUserId } from "../../../../../utils/notificationUserId";
import { getApiErrorMessage } from "../../../../../utils/utils";
import { CustomDateFormate } from "../../../../../utils/date";
interface Props {
  show: boolean;
  onClose: () => void;
  projectEvaluation: IProjectEvaluationListData;
  updateProjectEvaluation: (projectId: number, evaluators: IProjectEvaluator[]) => void;
}
const ProjectEvaluationModal: FC<Props> = ({
  show,
  onClose,
  projectEvaluation,
  updateProjectEvaluation
}) => {
  const { showSnackbar } = useSnackbar();
  const { auth } = useAuth();
  const currentUserId = resolveNumericUserId(auth);
  const [selectedEvaluators, setSelectedEvaluators] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const filteredEvaluators = useMemo(() => {
    return projectEvaluation.evaluators.filter((evaluator) =>
      evaluator.eval_status?.toLowerCase() !== "completed"
      && (currentUserId === null || evaluator.user_id !== currentUserId)
    );
  }, [projectEvaluation.evaluators, currentUserId]);
  
  const toggleSelectAll = (checked: boolean) => {
    setSelectedEvaluators(() =>
      checked ? filteredEvaluators.map((e) => e.user_id) : []
    );
  };

  const toggleEvaluatorSelection = (userId: number) => {
    setSelectedEvaluators((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };
  useEffect(() => {
    toggleSelectAll(true);
  }, [filteredEvaluators]);
  const handleSubmit = async () => {
    try{
      setSubmitting(true);
      if(selectedEvaluators.length > 0) {
      const response = await sendEvaluationReminderEmailsToAllEvaluators({project_id:projectEvaluation.project_id, evaluator_ids:selectedEvaluators});
      if (response.status === 'ok') {
          showSnackbar(response.message, "success");
          onClose();
          const updatedEvaluators = projectEvaluation.evaluators.map((evaluator) => {
            if(selectedEvaluators.includes(evaluator.user_id)) {
              return { ...evaluator, reminder_sent: evaluator.reminder_sent + 1 };
            }
            return evaluator;
          });
          updateProjectEvaluation(projectEvaluation.project_id,updatedEvaluators);
      } else {
          showSnackbar(response.message, "danger");
      }
      }else{
          showSnackbar("Please select at least one evaluator", "danger");
      }
    } catch (error) {
        console.error("Failed to send evaluation reminder emails:", error);
        showSnackbar(getApiErrorMessage(error, "Failed to send evaluation reminder emails"), "danger");
    }
    finally {
      setSubmitting(false);
    }
  }
  const isAllSelected = useMemo(() => {
    return filteredEvaluators.length > 0 && selectedEvaluators.length === filteredEvaluators.length;
  }, [selectedEvaluators, filteredEvaluators]);
  return (
    <AppModal
      show={show}
      title={`Send Reminder - ${projectEvaluation.project_name}`}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText="Send Reminder"
      showLoader={submitting}
      loadingText={"Sending reminders..."}
      width="800px"
      hideSubmitButton={filteredEvaluators.length === 0}
    >
        <div className="d-flex flex-column">
          <div className="d-flex justify-content-between">
            {/* Select All */}
            <div className="form-check form-check-custom form-check-solid mb-5">
              {filteredEvaluators.length > 0 && (
                <>
              <input
                type="checkbox"
                className="form-check-input"
                checked={isAllSelected}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
              <label className="form-check-label fw-semibold">
                Select All
              </label>
                </>
              )}
            </div>
            <div className="text-gray-900 fs-7">
              Due Date: <span className="fw-bold">{CustomDateFormate(projectEvaluation.eval_due_date)}</span>
            </div>
          </div>
            {/* Evaluator List */}
            <div style={{ maxHeight: "350px", overflowY: "auto" }} className="border rounded p-3">

              {filteredEvaluators.length === 0 ? (
                <div className="text-center text-muted py-5">
                  No pending or overdue evaluators to remind.
                </div>
              ) : filteredEvaluators.map((evaluator) => (

                <div key={evaluator.user_id} className="d-flex align-items-center justify-content-between py-3 border-bottom">

                  <div className="d-flex align-items-center">

                    <div className="form-check form-check-custom form-check-solid me-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedEvaluators.includes(evaluator.user_id)}
                        onChange={() => toggleEvaluatorSelection(evaluator.user_id)}
                      />
                    </div>

                    <div className="symbol symbol-35px symbol-circle me-3">
                      <span className="symbol-label bg-light-primary text-primary fw-bold">
                        {evaluator.initials}
                      </span>
                    </div>

                    <div>
                      <div className="fw-bold">{evaluator.first_name} {evaluator.last_name}</div>
                      <div className="text-muted fs-7">
                        Reminders Sent: {evaluator.reminder_sent || 0}
                      </div>
                    </div>

                  </div>

                  <div className="d-flex align-items-center gap-4">
                    <EvalStatus status={evaluator.eval_status} />                    
                  </div>

                </div>

              ))}

            </div>

        </div>
    </AppModal>
  );
};

export default ProjectEvaluationModal;